import React, {createContext, useReducer} from "react";
import {isNotNullOrEmpty, isNullOrEmpty, isValidEmail} from "../../shared/utils";
import SpeakerInvitationForm from "./SpeakerInvitation";
import {Speaker, SpeakerInvitationDay} from "../../model/SpeakerInvitationCreateMessage";
import {ParticipationType} from "../../model/ParticipationType";


export default function SpeakerInvitationWithValidation() {
    const [validationState, dispatch] = useReducer(validationReducer, createDefaultSpeakerInvitationValidationState());

    return (
        <ValidationContext.Provider value={validationState}>
            <ValidationContextDispatch.Provider value={dispatch}>
                <SpeakerInvitationForm />
            </ValidationContextDispatch.Provider>
        </ValidationContext.Provider>
    );
}

interface SpeakerValidationResult {
    uiId: number;
    firstNameValidationMessage: string;
    lastNameValidationMessage: string;
    emailValidationMessage: string;
    isSpeakerValid: boolean;
}

export interface SpeakerInvitationValidationState {
    invalidSpeakers: SpeakerValidationResult[];
    invalidDates: SpeakerInvitationDay[];
    isInputSectionValid: boolean | undefined;
    isDatesSectionValid: boolean | undefined;
}

export interface ValidationAction {
    type: string;
    changedDay: SpeakerInvitationDay | undefined;
    days: SpeakerInvitationDay[];
    speakers: Speaker[];
}

export const INPUT_CHANGED_ACTION = 'INPUT_CHANGED_ACTION';
export const DATE_CHANGED_ACTION = 'DATE_CHANGED_ACTION';
export const CHECK_BEFORE_SEND = 'CHECK_BEFORE_SEND';
export const CHECK_AFTER_SPEAKER_REMOVED = 'CHECK_AFTER_SPEAKER_REMOVED';

const validationReducer = (validationState: SpeakerInvitationValidationState, action: ValidationAction): SpeakerInvitationValidationState => {
    switch (action.type) {
        case INPUT_CHANGED_ACTION:
            return validateForm(action);

        case CHECK_AFTER_SPEAKER_REMOVED:
            return validateForm(action);

        case CHECK_BEFORE_SEND:
            return validateForm(action);

        case DATE_CHANGED_ACTION:
            return checkDatesSection(validationState, action);

        default:
            return validationState;
    }
}

export const ValidationContext = createContext<SpeakerInvitationValidationState>(createDefaultSpeakerInvitationValidationState());
export const ValidationContextDispatch = createContext<React.Dispatch<ValidationAction> | null>(null);

export const createDefaultValidationAction = () : ValidationAction => {
    return {
        type: '',
        changedDay: undefined,
        days: [],
        speakers: []
    };
}

function createDefaultSpeakerInvitationValidationState(): SpeakerInvitationValidationState {
    return {
        invalidSpeakers: [],
        invalidDates: [],
        isInputSectionValid: undefined,
        isDatesSectionValid: true
    };
}

function validateForm(action: ValidationAction): SpeakerInvitationValidationState {
    return {
        ...validateSpeakers(action.speakers),
        ...validateDays(action.days),
    };
}

interface SpeakersValidationResult {
    invalidSpeakers: SpeakerValidationResult[];
    isInputSectionValid: boolean;
}

function validateSpeakers(speakers: Speaker[]): SpeakersValidationResult {
    let invalidSpeakers: SpeakerValidationResult[] = [];
    let isInputSectionValid: boolean = true;

    speakers.forEach((s) => {
        const speakerValidationResult = validateSpeaker(s);
        if (!speakerValidationResult.isSpeakerValid) {
            invalidSpeakers = [...invalidSpeakers, speakerValidationResult];
            isInputSectionValid = false;
        }
    });

    return {
        invalidSpeakers,
        isInputSectionValid
    };
}

function validateSpeaker(speaker: Speaker): SpeakerValidationResult {
    let result: SpeakerValidationResult = {
        uiId: speaker.uiId,
        firstNameValidationMessage: validateFirstName(speaker.firstName),
        lastNameValidationMessage: validateLastName(speaker.lastName),
        emailValidationMessage: validateEmail(speaker.email),
        isSpeakerValid: false
    };

    result.isSpeakerValid = isNullOrEmpty(result.firstNameValidationMessage)
        && isNullOrEmpty(result.lastNameValidationMessage)
        && isNullOrEmpty(result.emailValidationMessage);

    return result;
}


interface ValidateDaysResult {
    invalidDates: SpeakerInvitationDay[];
    isDatesSectionValid: boolean;
}

function validateDays(days: SpeakerInvitationDay[]): ValidateDaysResult {
    let invalidDays: SpeakerInvitationDay[] = [];

    days.forEach(day => {
       if (!isDayValid(day)) {
           invalidDays = [...invalidDays, day];
       }
    });

    return invalidDays.length > 0
        ? {
            invalidDates: invalidDays,
            isDatesSectionValid: false
        } : {
            invalidDates: [],
            isDatesSectionValid: true
        }
}

function checkDatesSection(state: SpeakerInvitationValidationState, action: ValidationAction): SpeakerInvitationValidationState {
    let newValidationState = {
        ...state
    };

    const dateIsValid = isDayValid(action.changedDay!);

    if (dateIsValid) {
        newValidationState.invalidDates = [
            ...state.invalidDates.filter(d => d.date !== action.changedDay!.date)
        ];
    }

    if (!dateIsValid) {
        newValidationState.invalidDates = [
            ...state.invalidDates.filter(d => d.date !== action.changedDay!.date),
            action.changedDay!
        ];
    }

    newValidationState.isDatesSectionValid = newValidationState.invalidDates.length <= 0;

    return newValidationState;
}

function isDayValid(day: SpeakerInvitationDay): boolean {
    const checkedSlotsCount = day.slots.filter(s => s.checked).length ?? 0;
    return  day.participationType === ParticipationType.NotParticipate
        || checkedSlotsCount > 0;
}

function validateFirstName(firstName: string): string {
    return  !isFirstNameValid(firstName)
        ? 'first name should have at least one character and not more than 50 and have no special characters and numbers' : '';
}

function validateLastName(lastName: string): string {
    return  !isLastNameValid(lastName)
        ? 'last name should have at least one character and not more than 50 and have no special characters and numbers' : '';
}

function validateEmail(email: string): string {
    return  isValidEmail(email)
        ? '' : 'provided email address is not valid';
}

function isFirstNameValid (firstName: string): boolean {
    if (firstName === null || firstName === undefined) {
        return false;
    }

    const firstNameValidationRegex = /^(?=.*[a-zA-Z\p{L}])[a-zA-Z\s\-'`\p{L}]{1,50}$/u;
    return firstNameValidationRegex.test(firstName);
}

function isLastNameValid(lastName: string): boolean {
    if (lastName === null || lastName === undefined) {
        return false;
    }

    const lastNameValidationRegex = /^(?=.*[a-zA-Z\p{L}])[a-zA-Z\s\-'`\p{L}]{1,50}$/u;
    return lastNameValidationRegex.test(lastName);
}
